import type { CoastalSceneState } from './coastal-types';
import { surfCoastEdgeX, type SurfCoastGeometry } from './surf-shore';
import type { SurfBreakDirection } from './surf-break-direction';

export type SurfPoint = { x: number; y: number };
export type SurfWave = { progress: number; height: number; breaking: number; opacity: number; roughness: number; variation?: number; eventId?: number };

export const SURF_SHOREWARD = { x: 600, y: 220 };
export const SURF_HEADING = Math.atan2(SURF_SHOREWARD.y, SURF_SHOREWARD.x);
export const SURF_LINEUP = { along: 0.68, progress: 0.43 };
export const SURF_FACE_OFFSET = 0.095;
export const clampSurf = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));
export const easeSurf = (value: number) => { const t = clampSurf(value); return t * t * (3 - 2 * t); };

/** The same shoreline curve as the scenery mask, from the headland to the foreground. */
export function surfShorePoint(along: number, tide = 0.5, geometry?: SurfCoastGeometry): SurfPoint {
  const u = clampSurf(along);
  const v = 1 - u;
  if (geometry) {
    // Keep the established perspective and timing while fitting the selected coast.
    const fraction = (3 * v * v * u * 51 + 3 * v * u * u * 190 + u ** 3 * 327) / 327;
    const y = geometry.shoreStartY + fraction * (562 - geometry.shoreStartY);
    return { x: surfCoastEdgeX(geometry, y) + (tide - 0.5) * 48 * easeSurf(u / .15), y };
  }
  return {
    x: v ** 3 * 855 + 3 * v * v * u * 1000 + 3 * v * u * u * 938 + u ** 3 * 537 + (tide - 0.5) * 48,
    y: v ** 3 * 235 + 3 * v * v * u * 286 + 3 * v * u * u * 425 + u ** 3 * 562,
  };
}

/** Parallel curved crests move diagonally towards the visible beach, never down the screen independently. */
export function surfWaterPoint(along: number, progress: number, tide = 0.5, geometry?: SurfCoastGeometry): SurfPoint {
  const shore = surfShorePoint(along, tide, geometry);
  return { x: shore.x - SURF_SHOREWARD.x * (1 - progress), y: shore.y - SURF_SHOREWARD.y * (1 - progress) };
}

/** Wave travel is independent of the surfer. The surfer samples this surface. */
export function surfWaveAt(state: CoastalSceneState, phase: number): SurfWave {
  const progress = 0.05 + phase * 1.15;
  const growth = 0.16 + 0.84 * easeSurf((progress - 0.20) / 0.30);
  const collapse = 1 - easeSurf((progress - 0.84) / 0.22);
  return {
    progress,
    // Keep a fixed visual scale against the puppet, so small and overhead surf
    // remain visibly different without enlarging Colin to fit the wave.
    height: clampSurf(state.combinedWaveHeightM, 0, 5) * 52 * growth * collapse
      * (state.breakerStyle === 'plunging' ? 1.08 : state.breakerStyle === 'peeling' ? 0.95 : 1),
    breaking: easeSurf((progress - 0.39) / 0.12),
    opacity: easeSurf((progress - 0.08) / 0.13) * (1 - easeSurf((progress - 0.99) / 0.12)),
    roughness: 1 - state.cleanliness01,
  };
}

export function surfCrestProgress(wave: SurfWave, along: number): number {
  const variation = wave.variation ?? 0;
  return wave.progress + (Math.sin(along * 16 + variation) * 0.012
    + Math.sin(along * 37 - variation * 1.7) * 0.008) * wave.roughness;
}

/** The breaking section progresses in the same direction as the selected ride. */
export function surfPeelAt(wave: SurfWave, along: number, direction: SurfBreakDirection = 'left'): number {
  return clampSurf(wave.breaking * 1.5 - (direction === 'right' ? along : 1 - along) * .6);
}

export function surfWaveHeight(wave: SurfWave, along: number, progress: number): number {
  const distance = progress - surfCrestProgress(wave, along);
  // Rounded back and lip, steep middle face, then a smooth run into the trough.
  // Drawing and board contact use this same continuous profile.
  let profile = 0;
  if (distance < 0) profile = easeSurf((distance + 0.16) / 0.16);
  else if (distance < 0.015) profile = 1 - 0.04 * easeSurf(distance / 0.015);
  else if (distance < 0.18) profile = 0.96 - 0.88 * easeSurf((distance - 0.015) / 0.165);
  else profile = 0.08 * (1 - easeSurf((distance - 0.18) / 0.04));
  const ends = easeSurf(along / 0.12) * easeSurf((1 - along) / 0.08);
  const brokenFace = 1 + Math.sin(along * 23 + (wave.variation ?? 0)) * wave.roughness * 0.16;
  const perspective = (0.35 + along * 0.95) * easeSurf((along - 0.15) / 0.53);
  return Math.max(0, profile) * wave.height * perspective * ends * brokenFace;
}

export function surfSurfacePoint(wave: SurfWave, along: number, progress: number, tide = 0.5, geometry?: SurfCoastGeometry): SurfPoint {
  const point = surfWaterPoint(along, progress, tide, geometry);
  return { x: point.x, y: point.y - surfWaveHeight(wave, along, progress) };
}

/** Bound the water plane to the visible bay, while allowing a crest to rise above its horizon. */
export function surfVisibleWavePoint(wave: SurfWave, along: number, progress: number, tide = 0.5, geometry?: SurfCoastGeometry): SurfPoint {
  const bounded = Math.min(1, progress);
  const ground = surfWaterPoint(along, bounded, tide, geometry);
  const horizon = geometry?.horizonY ?? 181;
  return ground.y < horizon ? { x: ground.x, y: horizon }
    : surfSurfacePoint(wave, along, bounded, tide, geometry);
}

/** Statistical character of the selected sea state, never random weather. */
export function surfIrregularity(state: CoastalSceneState): number {
  const primary = state.swells.find((swell) => swell.kind === 'primary');
  const totalEnergy = state.swells.reduce((sum, swell) => sum + swell.heightM ** 2, 0);
  const mixedEnergy = state.swells.reduce((sum, swell) => {
    if (swell.kind === 'primary') return sum;
    const crossing = primary ? clampSurf(1 - Math.cos((swell.directionDeg - primary.directionDeg) * Math.PI / 180)) : 0;
    const periodSpread = primary ? clampSurf(Math.abs(swell.periodS - primary.periodS) / Math.max(1, primary.periodS)) : 0;
    return sum + swell.heightM ** 2 * (swell.kind === 'wind-wave' ? 1 : Math.max(crossing, periodSpread));
  }, 0) / Math.max(0.01, totalEnergy);
  const shortPeriod = clampSurf((11 - state.combinedPeriodS) / 7);
  return clampSurf((1 - state.cleanliness01) * 0.75 + mixedEnergy * 0.55 + shortPeriod * 0.18);
}

function seedNumber(seed: string): number {
  let value = 2166136261;
  for (let index = 0; index < seed.length; index++) value = Math.imul(value ^ seed.charCodeAt(index), 16777619);
  return value >>> 0;
}

function eventNoise(seed: number, event: number, channel: number): number {
  let value = Math.imul(seed ^ Math.imul(event, 374761393) ^ Math.imul(channel, 668265263), 1274126177);
  value = Math.imul(value ^ (value >>> 16), 2246822519);
  return ((value ^ (value >>> 13)) >>> 0) / 4294967295;
}

function eventStart(seed: number, cycle: number, member: number, irregularity: number): number {
  // The catch wave anchors Colin's session. Following crests vary without
  // changing direction or crossing each other, even at the roughest extreme.
  return cycle + member / 3 + (member === 0 ? 0 : (eventNoise(seed, cycle * 3 + member, 1) - 0.5) * (0.014 + irregularity * 0.15));
}

function eventWave(state: CoastalSceneState, seed: number, cycle: number, member: number, age: number, irregularity: number): SurfWave {
  const event = cycle * 3 + member;
  const group = Math.sin(cycle * 1.71 + seed % 37);
  const spread = 0.035 + irregularity * 0.28;
  const scale = 1 + (eventNoise(seed, event, 2) * 2 - 1) * spread + group * irregularity * 0.11;
  const wave = surfWaveAt(state, age);
  return { ...wave, height: wave.height * scale, roughness: Math.max(wave.roughness, irregularity * 0.7),
    variation: eventNoise(seed, event, 3) * Math.PI * 2, eventId: event };
}

/** Bounded neighbouring events make scrubbing/replay deterministic without a stored schedule. */
export function surfSetAt(state: CoastalSceneState, elapsed: number): { phase: number; wave: SurfWave; waves: SurfWave[] } {
  const time = elapsed / surfCycleSeconds(state);
  const cycle = Math.floor(time), phase = time - cycle;
  const seed = seedNumber(state.seed);
  const irregularity = surfIrregularity(state);
  const waves: SurfWave[] = [];
  for (let group = cycle - 1; group <= cycle; group++) {
    for (let member = 0; member < 3; member++) {
      const age = time - eventStart(seed, group, member, irregularity);
      if (age >= 0 && age < 1) waves.push(eventWave(state, seed, group, member, age, irregularity));
    }
  }
  return { phase, wave: eventWave(state, seed, cycle, 0, phase, irregularity), waves };
}

export function surfWavesAt(state: CoastalSceneState, phase: number): SurfWave[] {
  return surfSetAt(state, phase * surfCycleSeconds(state)).waves;
}

/** Swash reaches its furthest point after the wave reaches the beach, then drains back. */
export function surfSwashAt(wave: SurfWave, along: number, tide = 0.5, geometry?: SurfCoastGeometry): { edge: SurfPoint; shore: SurfPoint; opacity: number } {
  const age = clampSurf((wave.progress - 0.86) / 0.32);
  const pulse = Math.sin(Math.PI * age);
  const reach = pulse * (7 + Math.min(wave.height, 60) * 0.10);
  const shore = surfShorePoint(along, tide, geometry);
  const length = Math.hypot(SURF_SHOREWARD.x, SURF_SHOREWARD.y);
  const taper = easeSurf(along / 0.13) * easeSurf((1 - along) / 0.1);
  return { shore, edge: { x: shore.x + reach * SURF_SHOREWARD.x / length,
    y: shore.y + reach * SURF_SHOREWARD.y / length }, opacity: pulse * taper * 0.58 };
}

/** Returning and waiting surfers rise over every wave, not just their chosen one. */
export function surfSetSurfacePoint(waves: readonly SurfWave[], along: number, progress: number, tide = 0.5, geometry?: SurfCoastGeometry): SurfPoint {
  const point = surfWaterPoint(along, progress, tide, geometry);
  return { x: point.x, y: point.y - Math.max(0, ...waves.map((wave) => surfWaveHeight(wave, along, progress))) };
}

export function surfCycleSeconds(state: CoastalSceneState): number {
  return clampSurf(state.combinedPeriodS, 4, 20) * 2.8;
}
